import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';

import { useFragment } from '@libs/trip-planner-interfaces';
import type {
  FragmentType,
  GraphQL,
  LuggageInformation,
  TransportLeg,
  TransportSegment,
  LuggageType,
  LuggageUnit,
} from '@libs/trip-planner-interfaces';

import { LegFragment } from '../../queries';
import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';

/**
 * Type guard to safely convert GraphQL LuggageTypeResult to LuggageType
 */
function toLuggageType(
  type: GraphQL.LuggageTypeResult | null | undefined
): LuggageType | undefined {
  if (!type) {
    return undefined;
  }

  // Validate that the GraphQL enum value matches our TypeScript enum
  // Using string literals to avoid const enum access issues with isolatedModules
  const validTypes: Record<string, LuggageType> = {
    CabinBag: 'CabinBag' as LuggageType,
    CheckedBag: 'CheckedBag' as LuggageType,
    None: 'None' as LuggageType,
  };

  return validTypes[type];
}

/**
 * Type guard to safely convert GraphQL LuggageUnitTypeResult to LuggageUnit
 */
function toLuggageUnit(
  unit: GraphQL.LuggageUnitTypeResult | null | undefined
): LuggageUnit | undefined {
  if (!unit) {
    return undefined;
  }

  // Validate that the GraphQL enum value matches our TypeScript enum
  // Using string literals to avoid const enum access issues with isolatedModules
  const validUnits: Record<string, LuggageUnit> = {
    Kilograms: 'Kilograms' as LuggageUnit,
    KilogramsAndPieces: 'KilogramsAndPieces' as LuggageUnit,
    None: 'None' as LuggageUnit,
    Pieces: 'Pieces' as LuggageUnit,
  };

  return validUnits[unit];
}

type LegFragmentQL = FragmentType<
  DocumentTypeDecoration<GraphQL.LegFragment, unknown>
>;
export function toLeg(legFragmentQL: LegFragmentQL): TransportLeg {
  const legFragment = useFragment(LegFragment, legFragmentQL);

  assertSmartSearchList(legFragment.segments);

  return {
    durationInMinutes: legFragment.durationInMinutes,
    platingCarrier: legFragment.platingCarrier
      ? legFragment.platingCarrier
      : undefined,
    segments: legFragment.segments.map(toSegment),
    luggageInformation: legFragment.luggageInformation?.length
      ? legFragment.luggageInformation
          .filter(
            (luggage): luggage is NonNullable<typeof luggage> =>
              luggage !== null
          )
          .map(toLuggageInformation)
      : undefined,
  };
}

function toLuggageInformation(
  luggageQL: NonNullable<
    NonNullable<GraphQL.LegFragment['luggageInformation']>[number]
  >
): LuggageInformation {
  return {
    included: luggageQL.included,
    pieces: luggageQL.pieces ?? undefined,
    type: toLuggageType(luggageQL.type),
    unit: toLuggageUnit(luggageQL.unit),
    weight: luggageQL.weight ?? undefined,
  };
}

type SegmentsQL = NonNullable<GraphQL.LegFragment['segments']>;
type SegmentQL = NonNullable<SegmentsQL[number]>;
function toSegment(segmentQL: SegmentQL): TransportSegment {
  assertSmartSearchValue(segmentQL.layoverTimeUntilNextSegmentInMinutes);
  assertSmartSearchValue(segmentQL.transportType);
  assertSmartSearchValue(segmentQL.arrivalIata);
  assertSmartSearchValue(segmentQL.carrier);
  assertSmartSearchValue(segmentQL.departureIata);
  assertSmartSearchValue(segmentQL.operatingCarrier);
  assertSmartSearchValue(segmentQL.transportId);

  return {
    arrivalDateTime: new Date(segmentQL.arrivalDateTime),
    arrivalIata: segmentQL.arrivalIata,
    carrier: segmentQL.carrier,
    departureDateTime: new Date(segmentQL.departureDateTime),
    departureIata: segmentQL.departureIata,
    isCabinMandatory: segmentQL.isCabinMandatory,
    layoverTimeUntilNextSegmentInMinutes:
      segmentQL.layoverTimeUntilNextSegmentInMinutes,
    operatingCarrier: segmentQL.operatingCarrier,
    transportId: segmentQL.transportId,
    transportType: segmentQL.transportType,
    durationInMinutes: segmentQL.durationInMinutes,
    transportationName: segmentQL.transportationName ?? undefined,
    vehicles: segmentQL.vehicles?.map((vehicle) => {
      assertSmartSearchValue(vehicle);
      return vehicle;
    }),
  };
}
